'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';

export interface Agent {
  id: string;
  name: string;
  role: string;
  bio?: string;
  image?: string;
  email?: string;
  phone?: string;
}

export interface StoneInvestmentAgentsSectionProps {
  title?: string;
  subtitle?: string;
  agents: Agent[];
  columns?: 2 | 3;
  messages?: Record<string, unknown>;
}

/**
 * Stone Investment Agents Section
 * 
 * Elegant team member showcase with:
 * - Square image frames
 * - Greyscale to color effect
 * - Sophisticated typography
 */
export function StoneInvestmentAgentsSection({ 
  title: titleProp = 'Our Experts',
  subtitle: subtitleProp,
  agents: agentsProp,
  columns = 2,
  messages,
}: StoneInvestmentAgentsSectionProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const agentsMessages = (stoneMessages.agentsSection || {}) as Record<string, unknown>;
  const translatedAgents = agentsMessages.agents as Array<{ role: string; bio: string }> | undefined;

  const title = (agentsMessages.title as string) || titleProp;
  const subtitle = (agentsMessages.subtitle as string) || subtitleProp;
  const eyebrow = (agentsMessages.eyebrow as string) || 'Team';

  // Merge translated agent data with prop-based agents (preserving name, email, phone, image)
  const agents = agentsProp.map((agent, idx) => {
    const translated = translatedAgents?.[idx];
    if (translated) {
      return { ...agent, role: translated.role || agent.role, bio: translated.bio || agent.bio };
    }
    return agent;
  });

  if (!agents || agents.length === 0) {
    return null;
  }

  return (
    <section className="py-24 lg:py-32 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 lg:mb-20"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-px bg-[var(--color-text)]" />
            <span 
              className="text-xs tracking-[0.3em] uppercase text-[var(--color-text-muted)]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {eyebrow}
            </span>
          </div>
          
          <h2 
            className="text-4xl lg:text-5xl xl:text-6xl text-[var(--color-text)] tracking-[-0.02em] mb-6"
            style={{ 
              fontFamily: "'Cormorant Garamond', 'Georgia', serif",
              fontWeight: 300,
            }}
          >
            {title}
          </h2>
          
          {subtitle && (
            <p 
              className="text-[var(--color-text-muted)] text-base lg:text-lg max-w-2xl"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {subtitle}
            </p>
          )}
        </motion.div>

        {/* Agents Grid */}
        <div className={`grid grid-cols-1 ${columns === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-12 lg:gap-16`}>
          {agents.map((agent, index) => (
            <motion.div
              key={agent.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* Image */}
                <div className="relative aspect-[3/4] overflow-hidden bg-[#1a1a1a]">
                  {agent.image ? (
                    <Image
                      src={agent.image}
                      alt={agent.name}
                      fill
                      className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a] flex items-center justify-center">
                      <span 
                        className="text-4xl text-white/20"
                        style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
                      >
                        {agent.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="lg:pt-8">
                  <h3 
                    className="text-2xl lg:text-3xl text-[var(--color-text)] mb-2"
                    style={{ 
                      fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                      fontWeight: 400,
                    }}
                  >
                    {agent.name}
                  </h3>
                  
                  <p 
                    className="text-xs tracking-[0.2em] uppercase text-[var(--color-text-muted)] mb-6"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {agent.role}
                  </p>
                  
                  {agent.bio && (
                    <p 
                      className="text-[var(--color-text-muted)] text-sm leading-relaxed mb-6"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {agent.bio}
                    </p>
                  )}
                  
                  <div className="space-y-2">
                    {agent.email && (
                      <a 
                        href={`mailto:${agent.email}`}
                        className="block text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors duration-300"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {agent.email}
                      </a>
                    )}
                    {agent.phone && (
                      <a 
                        href={`tel:${agent.phone.replace(/\s/g, '')}`}
                        className="block text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors duration-300"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {agent.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
